import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, DollarSign } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import StockParametersSection from './StockParametersSection';
import TaxParametersSection from './TaxParametersSection';
import GrowthSection from './GrowthSection';
import ProjectionSection from './ProjectionSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент основной формы расчета дивидендов
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  schedule,
  setSchedule,
  dividendFrequencyTypes,
  taxResidencyTypes,
  currencyTypes,
  savePortfolio 
}) => {
  
  // Состояние для имени сохраняемого портфеля
  const [savePortfolioName, setSavePortfolioName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем ставку НДФЛ автоматически при изменении налогового резидентства
      if (name === 'taxResidency') {
        const selectedTaxType = taxResidencyTypes.find(type => type.id === fieldValue);
        if (selectedTaxType) {
          const currentRate = parseFloatOrDefault(prev.taxRate, 0);
          const isRateDefault = taxResidencyTypes.some(tt => currentRate === tt.defaultRate);
          if (isRateDefault || !prev.taxRate || currentRate <= 0) {
            newState.taxRate = selectedTaxType.defaultRate.toString();
          }
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeDividendGrowth' && !checked) {
        newState.dividendGrowthRate = '5';
      }
      
      if (name === 'includeReinvestment' && !checked) {
        newState.reinvestmentRate = '100';
      }
      
      if (name === 'includeStockGrowth' && !checked) {
        newState.stockGrowthRate = '7';
      }

      if (name === 'hasDoubleTaxTreaty' && !checked) {
        newState.foreignWithholdingTax = '10';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [taxResidencyTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      stockPrice, sharesCount, dividendPerShare, dividendFrequency, currency, startDate,
      taxRate, taxResidency, hasDoubleTaxTreaty, foreignWithholdingTax,
      includeDividendGrowth, dividendGrowthRate, includeReinvestment, reinvestmentRate,
      projectionPeriod, includeStockGrowth, stockGrowthRate
    } = formData;

    // Парсинг числовых значений
    const numStockPrice = parseFloatOrDefault(stockPrice, -1);
    const numSharesCount = parseFloatOrDefault(sharesCount, -1);
    const numDividendPerShare = parseFloatOrDefault(dividendPerShare, -1);
    const numTaxRate = parseFloatOrDefault(taxRate, -1);
    const numProjectionPeriod = parseFloatOrDefault(projectionPeriod, -1);

    // Валидация базовых полей
    if (numStockPrice <= 0) newErrors.stockPrice = 'Цена акции должна быть положительной.';
    if (!newErrors.stockPrice && numStockPrice > 1000000) newErrors.stockPrice = 'Максимальная цена акции 1 000 000.';
    
    if (numSharesCount <= 0) newErrors.sharesCount = 'Количество акций должно быть положительным.';
    if (!newErrors.sharesCount && !isInteger(sharesCount)) newErrors.sharesCount = 'Количество акций должно быть целым числом.';
    if (!newErrors.sharesCount && numSharesCount > 1000000) newErrors.sharesCount = 'Максимальное количество акций 1 000 000.';
    
    if (numDividendPerShare < 0) newErrors.dividendPerShare = 'Дивиденд не может быть отрицательным.';
    
    // Валидация налоговых параметров
    if (numTaxRate < 0) newErrors.taxRate = 'Ставка НДФЛ не может быть отрицательной.';
    if (!newErrors.taxRate && numTaxRate > 100) newErrors.taxRate = 'Ставка НДФЛ не может быть более 100%.';
    
    if (!taxResidency) newErrors.taxResidency = 'Выберите налоговый статус.';
    
    if (hasDoubleTaxTreaty) {
      const numForeignTax = parseFloatOrDefault(foreignWithholdingTax, -1);
      if (numForeignTax < 0) newErrors.foreignWithholdingTax = 'Ставка не может быть отрицательной.';
      if (!newErrors.foreignWithholdingTax && numForeignTax > 100) newErrors.foreignWithholdingTax = 'Ставка не может быть более 100%.';
    }
    
    // Валидация параметров роста и реинвестирования
    if (includeDividendGrowth) {
      const numGrowthRate = parseFloatOrDefault(dividendGrowthRate, -1);
      if (numGrowthRate < 0) newErrors.dividendGrowthRate = 'Рост не может быть отрицательным.';
      if (!newErrors.dividendGrowthRate && numGrowthRate > 100) newErrors.dividendGrowthRate = 'Рост не может быть более 100% в год.';
    }
    
    if (includeReinvestment) {
      const numReinvestRate = parseFloatOrDefault(reinvestmentRate, -1);
      if (numReinvestRate < 0) newErrors.reinvestmentRate = 'Процент не может быть отрицательным.';
      if (!newErrors.reinvestmentRate && numReinvestRate > 100) newErrors.reinvestmentRate = 'Процент не может быть более 100%.';
    }
    
    // Валидация параметров прогнозирования
    if (numProjectionPeriod <= 0) newErrors.projectionPeriod = 'Период должен быть положительным.';
    if (!newErrors.projectionPeriod && numProjectionPeriod > 50) newErrors.projectionPeriod = 'Максимальный период 50 лет.';
    
    if (includeStockGrowth) {
      const numStockGrowth = parseFloatOrDefault(stockGrowthRate, -1);
      if (numStockGrowth < -100) newErrors.stockGrowthRate = 'Рост не может быть менее -100%.';
      if (!newErrors.stockGrowthRate && numStockGrowth > 100) newErrors.stockGrowthRate = 'Рост не может быть более 100% в год.';
    }
    
    // Проверка даты начала инвестирования
    if (!startDate) newErrors.startDate = 'Укажите дату начала инвестирования.';
    try {
      const dateObject = new Date(startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета графика дивидендных выплат и итоговых результатов
  const calculateDividendSchedule = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const stockPrice = parseFloatOrDefault(formData.stockPrice);
        const sharesCount = parseIntOrDefault(formData.sharesCount);
        const dividendPerShare = parseFloatOrDefault(formData.dividendPerShare);
        const dividendFrequency = formData.dividendFrequency;
        const currency = formData.currency;
        const taxRate = parseFloatOrDefault(formData.taxRate) / 100;
        const hasDoubleTaxTreaty = formData.hasDoubleTaxTreaty;
        const foreignWithholdingTax = hasDoubleTaxTreaty ? parseFloatOrDefault(formData.foreignWithholdingTax) / 100 : 0;
        const includeDividendGrowth = formData.includeDividendGrowth;
        const dividendGrowthRate = includeDividendGrowth ? parseFloatOrDefault(formData.dividendGrowthRate) / 100 : 0;
        const includeReinvestment = formData.includeReinvestment;
        const reinvestmentRate = includeReinvestment ? parseFloatOrDefault(formData.reinvestmentRate) / 100 : 0;
        const projectionPeriod = parseIntOrDefault(formData.projectionPeriod);
        const includeStockGrowth = formData.includeStockGrowth;
        const stockGrowthRate = includeStockGrowth ? parseFloatOrDefault(formData.stockGrowthRate) / 100 : 0;
        const startDate = new Date(formData.startDate);
        
        // Получаем количество выплат в год в зависимости от периодичности
        const frequency = dividendFrequencyTypes.find(f => f.id === dividendFrequency);
        const paymentsPerYear = frequency ? frequency.periodsPerYear : 1;
        
        // Находим подходящий символ валюты
        const currencyObj = currencyTypes.find(c => c.id === currency);
        const currencySymbol = currencyObj ? currencyObj.symbol : '₽';
        
        // --- Инициализируем переменные для расчета ---
        let currentSharesCount = sharesCount;
        let currentStockPrice = stockPrice;
        let currentDividendPerShare = dividendPerShare;
        let totalDividends = 0;
        let totalTaxesPaid = 0;
        let totalNetDividends = 0;
        let totalReturnRate = 0;
        let totalInitialInvestment = stockPrice * sharesCount;
        let totalFinalValue = totalInitialInvestment;
        
        const calculatedSchedule = [];
        
        // --- Расчет графика дивидендных выплат ---
        // Рассчитываем количество периодов выплат
        const totalPaymentPeriods = projectionPeriod * paymentsPerYear;
        
        for (let period = 1; period <= totalPaymentPeriods; period++) {
          // Расчет даты выплаты
          const paymentDate = new Date(startDate);
          paymentDate.setMonth(startDate.getMonth() + Math.floor((period * 12) / paymentsPerYear));
          
          // Расчет дивиденда на период
          let periodDividend = currentDividendPerShare;
          
          // Если включен рост дивидендов, учитываем его
          if (includeDividendGrowth && period > paymentsPerYear) {
            // Рост применяется ежегодно
            const yearsSinceStart = Math.floor((period - 1) / paymentsPerYear);
            periodDividend = dividendPerShare * Math.pow(1 + dividendGrowthRate, yearsSinceStart);
          }
          
          // Расчет выплаты без налогов
          const grossDividendPayment = periodDividend * currentSharesCount;
          
          // Расчет налогов с учетом соглашения об избежании двойного налогообложения
          let taxAmount = 0;
          if (hasDoubleTaxTreaty) {
            // Налог, удерживаемый в стране источника дивидендов
            const foreignTaxAmount = grossDividendPayment * foreignWithholdingTax;
            
            // Налог в РФ с учетом зачета налога, уплаченного за рубежом
            const rusTaxAmount = grossDividendPayment * taxRate;
            
            // Итоговый налог = налог в РФ минус налог за рубежом, но не менее 0
            taxAmount = Math.max(0, rusTaxAmount - foreignTaxAmount) + foreignTaxAmount;
          } else {
            // Стандартный расчет налога
            taxAmount = grossDividendPayment * taxRate;
          }
          
          // Чистая выплата после налогов
          const netDividendPayment = grossDividendPayment - taxAmount;
          
          // Расчет реинвестирования
          let reinvestedAmount = 0;
          if (includeReinvestment) {
            reinvestedAmount = netDividendPayment * reinvestmentRate;
            
            // Рассчитываем, сколько акций можно купить на реинвестируемую сумму
            const newShares = reinvestedAmount / currentStockPrice;
            currentSharesCount += newShares;
          }
          
          // Обновляем цену акции для следующего периода (если включен рост)
          if (includeStockGrowth) {
            // Рост применяется пропорционально к временному интервалу
            const growthPerPeriod = Math.pow(1 + stockGrowthRate, 1 / paymentsPerYear) - 1;
            currentStockPrice *= (1 + growthPerPeriod);
          }
          
          // Обновляем общие суммы
          totalDividends += grossDividendPayment;
          totalTaxesPaid += taxAmount;
          totalNetDividends += netDividendPayment;
          
          // Записываем данные для периода
          calculatedSchedule.push({
            period,
            date: paymentDate,
            sharesCount: currentSharesCount,
            dividendPerShare: periodDividend,
            grossDividend: grossDividendPayment,
            tax: taxAmount,
            netDividend: netDividendPayment,
            reinvestedAmount: reinvestedAmount,
            stockPrice: currentStockPrice,
            portfolioValue: currentSharesCount * currentStockPrice
          });
        }
        
        // Расчет итоговой стоимости портфеля
        totalFinalValue = currentSharesCount * currentStockPrice;
        
        // Расчет годовой доходности
        const annualReturnRate = Math.pow(totalFinalValue / totalInitialInvestment, 1 / projectionPeriod) - 1;
        
        // Расчет общей доходности за весь период
        totalReturnRate = (totalFinalValue - totalInitialInvestment + totalNetDividends - (includeReinvestment ? totalNetDividends * reinvestmentRate : 0)) / totalInitialInvestment;
        
        // --- Подготовка итоговых результатов ---
        const finalResults = {
          initialInvestment: totalInitialInvestment,
          initialSharesCount: sharesCount,
          initialStockPrice: stockPrice,
          initialDividendPerShare: dividendPerShare,
          
          finalStockPrice: currentStockPrice,
          finalSharesCount: currentSharesCount,
          finalPortfolioValue: totalFinalValue,
          
          totalDividends: totalDividends,
          totalTaxesPaid: totalTaxesPaid,
          totalNetDividends: totalNetDividends,
          
          dividendYield: (dividendPerShare * paymentsPerYear) / stockPrice,
          annualReturn: annualReturnRate,
          totalReturn: totalReturnRate,
          
          yearsProjected: projectionPeriod,
          paymentsFrequency: dividendFrequency,
          paymentsPerYear: paymentsPerYear,
          
          currency: currency,
          currencySymbol: currencySymbol,
          taxRate: taxRate,
          
          scheduleShort: calculatedSchedule.slice(0, 12) // Первые 12 периодов для предпросмотра
        };
        
        setResults(finalResults);
        setSchedule(calculatedSchedule);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setSchedule([]);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setSchedule, setIsCalculating, dividendFrequencyTypes, currencyTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      stockPrice: '1000',
      sharesCount: '100',
      dividendPerShare: '50',
      dividendFrequency: 'quarterly',
      currency: 'RUB',
      
      taxRate: '13',
      taxResidency: 'resident',
      hasDoubleTaxTreaty: false,
      foreignWithholdingTax: '10',
      
      includeDividendGrowth: false,
      dividendGrowthRate: '5',
      includeReinvestment: false,
      reinvestmentRate: '100',
      
      projectionPeriod: '5',
      includeStockGrowth: false,
      stockGrowthRate: '7',
      
      startDate: new Date().toISOString().split('T')[0],
    });
    setResults(null);
    setSchedule([]);
    setErrors({});
    setSavePortfolioName('');
    setShowSaveDialog(false);
  };

  // Экспорт графика выплат в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Период', 'Дата выплаты', 'Количество акций', 'Дивиденд на акцию', 'Общая сумма дивидендов', 'Налоги', 'Дивиденды после налогов'];
    if (formData.includeReinvestment) headers.push('Реинвестировано');
    headers.push('Цена акции', 'Стоимость портфеля');

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = schedule.map(item => {
      const rowData = [
        item.period,
        item.date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        formatCsvNumber(item.sharesCount),
        formatCsvNumber(item.dividendPerShare),
        formatCsvNumber(item.grossDividend),
        formatCsvNumber(item.tax),
        formatCsvNumber(item.netDividend),
      ];
      if (formData.includeReinvestment) rowData.push(formatCsvNumber(item.reinvestedAmount));
      rowData.push(
        formatCsvNumber(item.stockPrice),
        formatCsvNumber(item.portfolioValue)
      );
      return rowData.join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(results.finalSharesCount),
      '',
      formatCsvNumber(results.totalDividends),
      formatCsvNumber(results.totalTaxesPaid),
      formatCsvNumber(results.totalNetDividends),
    ];
    if (formData.includeReinvestment) totalRow.push(formatCsvNumber(results.totalNetDividends * parseFloat(formData.reinvestmentRate) / 100));
    totalRow.push(
      formatCsvNumber(results.finalStockPrice),
      formatCsvNumber(results.finalPortfolioValue)
    );
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Начальные инвестиции:', formatCsvNumber(results.initialInvestment)],
      ['Количество акций:', results.initialSharesCount.toString()],
      ['Начальная цена акции:', formatCsvNumber(results.initialStockPrice)],
      ['Дивиденд на акцию:', formatCsvNumber(results.initialDividendPerShare)],
      ['Периодичность выплат:', formData.dividendFrequency === 'annually' ? 'Ежегодно' : 
                                formData.dividendFrequency === 'semi_annually' ? 'Раз в полгода' : 
                                formData.dividendFrequency === 'quarterly' ? 'Ежеквартально' : 'Ежемесячно'],
      ['Ставка НДФЛ:', `${(results.taxRate * 100).toFixed(1)}%`],
      ['Период прогнозирования:', `${results.yearsProjected} лет`],
      ['Общая доходность:', `${(results.totalReturn * 100).toFixed(2)}%`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'дивидендный_портфель.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения портфеля
  const handleSavePortfolio = () => {
    if (showSaveDialog) {
      const savedResult = savePortfolio(savePortfolioName || null);
      if (savedResult) {
        alert(`Портфель "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSavePortfolioName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateDividendSchedule();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-green-500" />
          Параметры расчета дивидендов
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры акций */}
        <StockParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          dividendFrequencyTypes={dividendFrequencyTypes}
          currencyTypes={currencyTypes}
        />

        {/* Налоговые параметры */}
        <TaxParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          taxResidencyTypes={taxResidencyTypes}
        />

        {/* Параметры роста и реинвестирования */}
        <GrowthSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Параметры прогнозирования */}
        <ProjectionSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Диалог сохранения портфеля */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={savePortfolioName}
                onChange={(e) => setSavePortfolioName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSavePortfolio}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать дивиденды'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSavePortfolio}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить портфель
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;